import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineXL } from '@sorare/core/src/atoms/typography';
import { Arrows } from '@sorare/core/src/components/rivals/Seasons/SeasonLeaderboardList/Arrows';
import useTransitionApi from '@sorare/core/src/hooks/ui/useTransitionApi';
import { glossary } from '@sorare/core/src/lib/glossary';

import { RewardBoxDialog } from '@sorare/gameplay/src/components/rewards/RewardBoxDialog';

import { BaseLayout } from 'components/rivals/PromotionRelegationStory/BaseLayout';

import { Promoted_footballRivalsPromotionRelegationStory } from './__generated__/index.graphql';

type Props = {
  story: Promoted_footballRivalsPromotionRelegationStory;
  onDismiss: () => void;
};
export const Promoted = ({ story, onDismiss }: Props) => {
  const [step, setStep] = useState<'intro' | 'reveal'>('intro');
  const [rewardBoxDialogOpen, setRewardBoxDialogOpen] = useState(false);
  const { newDivision, previousDivision, rewardProbabilisticBundle } = story;
  const onCloseRewardBoxDialog = () => {
    setRewardBoxDialogOpen(false);
    onDismiss();
  };

  const { updateDOM } = useTransitionApi();

  if (step === 'intro') {
    return (
      <BaseLayout
        backgroundType="pink"
        title={
          <HeadlineXL>
            <FormattedMessage
              id="PromotionRelegationStory.Promoted.titleIntro"
              defaultMessage="Claim your glory"
            />
          </HeadlineXL>
        }
        badgeUrl={previousDivision.badgeUrl}
        divisionTitle={previousDivision.title}
        ctas={
          <Vertical gap={1} center>
            <Button
              size="medium"
              color="secondary"
              onClick={() => updateDOM(() => setStep('reveal'))}
            >
              <FormattedMessage {...glossary.continue} />
            </Button>
            <Button size="medium" color="tertiary" onClick={onDismiss}>
              <FormattedMessage {...glossary.dismiss} />
            </Button>
          </Vertical>
        }
      />
    );
  }

  return (
    <>
      <BaseLayout
        backgroundType="blue"
        title={
          <Horizontal gap={4}>
            <Arrows />
            <HeadlineXL>
              <FormattedMessage
                id="PromotionRelegationStory.Promoted.title"
                defaultMessage="Time to dominate!"
              />
            </HeadlineXL>
            <Arrows />
          </Horizontal>
        }
        badgeUrl={newDivision.badgeUrl}
        divisionTitle={newDivision.title}
        ctas={
          <Vertical gap={1} center>
            {!!rewardProbabilisticBundle && (
              <Button
                size="medium"
                color="secondary"
                onClick={() => {
                  setRewardBoxDialogOpen(true);
                }}
              >
                <FormattedMessage
                  id="PromotionRelegationStory.Promoted.claimReward"
                  defaultMessage="Claim Reward"
                />
              </Button>
            )}
            <Button size="medium" color="tertiary" onClick={onDismiss}>
              <FormattedMessage {...glossary.dismiss} />
            </Button>
          </Vertical>
        }
      />
      {rewardProbabilisticBundle && (
        <RewardBoxDialog
          open={rewardBoxDialogOpen}
          onClose={onCloseRewardBoxDialog}
          rewardBoxes={[rewardProbabilisticBundle]}
        />
      )}
    </>
  );
};

Promoted.fragments = {
  footballRivalsPromotionRelegationStory: gql`
    fragment Promoted_footballRivalsPromotionRelegationStory on FootballRivalsPromotionRelegationStory {
      id
      rewardProbabilisticBundle {
        id
        ...RewardBoxDialog_ProbabilisticBundle
      }
      previousDivision {
        id
        title
        badgeUrl
      }
      newDivision {
        id
        title
        badgeUrl
      }
    }
    ${RewardBoxDialog.fragments.ProbabilisticBundle}
  ` as TypedDocumentNode<Promoted_footballRivalsPromotionRelegationStory>,
};
